(ns frontend.ui.component.file-input.event
  (:require
   [ajax.core :as ajax :refer [json-response-format]]
   [frontend.controller.websocket.event :as ws]
   [re-frame.core :as rf]
   [potpuri.core :as p]))

(defn- gen-tmp-attachment
  "Generate a map describing an attachment being uploaded. This map will be
  appended to some existing entity's key `:attachments`. When upload is done it
  will be replaced by the actual attachment returned by the upload process."
  [tmp-id {file :file}]
  {:db/id           tmp-id
   :attachment/filename (.-name file)
   :upload/status   :initializing
   :upload/percents 0})

(rf/reg-event-fx
 ::upload-file
 (fn [{db :db}
      [_ owning-entity-ref-path attachments-ref-path
       dispatch-on-upload-success dispatch-on-upload-progress upload-desc]]
   (let [tmp-attachment-id (str "upload-file-" (rand-int 1000000000))
         tmp-attachment (gen-tmp-attachment tmp-attachment-id upload-desc)
         tmp-attachment-path (conj attachments-ref-path tmp-attachment-id)
         owning-entity (get-in db owning-entity-ref-path)
         updated-entity (update owning-entity :common/attachments conj tmp-attachment-id)
         updated-db (assoc-in db owning-entity-ref-path updated-entity)
         updated-db (assoc-in updated-db tmp-attachment-path tmp-attachment)]
     {:db updated-db
      :fx [[:dispatch
            [::get-token
             [::upload-file-with-token
              owning-entity-ref-path
              attachments-ref-path
              tmp-attachment-id
              upload-desc
              dispatch-on-upload-success
              dispatch-on-upload-progress]
             [::upload-file-failure
              attachments-ref-path
              tmp-attachment-id
              (:dispatch-on-upload-failure upload-desc)]]]]})))

(rf/reg-event-fx
 ::get-token
 (fn [_ [_ {:keys [on-success on-failure]}]]
   {:http-xhrio {:method          :post
                 :uri             "/lp-portal/api/create-op-token"
                 :params          {}
                 :timeout         30000
                 :format          (ajax/transit-request-format)
                 :response-format (ajax/transit-response-format)
                 :on-success      on-success
                 :on-failure      on-failure}}))

(rf/reg-event-fx
 ::upload-file-socket
 (fn [{db :db}
      [_ owning-entity-ref-path attachments-ref-path
       dispatch-on-upload-success dispatch-on-upload-progress upload-desc]]
   (let [tmp-attachment-id (str "upload-file-" (rand-int 1000000000))
         tmp-attachment (gen-tmp-attachment tmp-attachment-id upload-desc)
         tmp-attachment-path (conj attachments-ref-path tmp-attachment-id)
         owning-entity (get-in db owning-entity-ref-path)
         updated-entity (update owning-entity :common/attachments conj tmp-attachment-id)
         updated-db (assoc-in db owning-entity-ref-path updated-entity)
         updated-db (assoc-in updated-db tmp-attachment-path tmp-attachment)]
     {:db updated-db
      :fx [[:dispatch
            [::get-token-socket
             {:on-success [::upload-file-with-token
                           owning-entity-ref-path
                           attachments-ref-path
                           tmp-attachment-id
                           upload-desc
                           dispatch-on-upload-success
                           dispatch-on-upload-progress]
              :on-failure [::upload-file-failure
                           attachments-ref-path
                           tmp-attachment-id
                           (:dispatch-on-upload-failure upload-desc)]}]]]})))

(rf/reg-event-fx
 ::get-token-socket
 (fn [_ [_ {:keys [on-success on-failure]}]]
   {::ws/send
    {:id         :token/create-op-token
     :body       nil
     :on-success on-success
     :on-error   on-failure}}))

(defn- ->upload-body
  "Generate the body to send a file along with a token (`cljs-ajax` – used by
  `re-frame-http-fx` – requires files to be passed as form data.
  See https://github.com/JulianBirch/cljs-ajax#getpost-examples"
  [{:keys [file owning-entity-id]} token]
  (doto
   (js/FormData.)
   (.append "token" token)
   (.append "filename" (.-name file))
   (.append "file" file)
   ;; DB entity owning this file as attachment
   (.append "target" owning-entity-id)))

(rf/reg-event-fx
 ::upload-file-with-token
 (fn [_
      [_ owning-entity-ref-path attachments-ref-path tmp-attachment-id upload-desc
       dispatch-on-upload-success dispatch-on-upload-progress
       token-result]]
   {:http-xhrio
    {:method          :post
     :uri             "/attachment/api/add-attachment"
     :body            (->upload-body upload-desc
                                     (-> (get-in token-result [:body :token])))
     :response-format (json-response-format {:keywords? true})
     ;; FIXME this is "bad": no side-effect in event handling!
     :progress-handler #(rf/dispatch [::upload-progress dispatch-on-upload-progress
                                      attachments-ref-path tmp-attachment-id %])
     :on-success      [::upload-file-success
                       owning-entity-ref-path
                       attachments-ref-path
                       tmp-attachment-id
                       dispatch-on-upload-success]
     :on-failure      [::upload-file-failure
                       attachments-ref-path
                       tmp-attachment-id
                       (:dispatch-on-upload-failure upload-desc)]}}))

(rf/reg-event-fx
 ::remove-file
 (fn [{db :db} [_ file-id owning-entity-ref-path attachments-ref-path]]
   (let [optimistic-db (p/dissoc-in db (conj attachments-ref-path file-id))
         owning-entity (get-in db owning-entity-ref-path)
         attachment-ids-set (set (:common/attachments owning-entity))
         updated-entity (assoc owning-entity :common/attachments (disj attachment-ids-set file-id))
         optimistic-db (assoc-in optimistic-db owning-entity-ref-path updated-entity)]
     {::ws/send {:id         :entity/disable!
                 :body       {:entity-id file-id}
                 :on-success [::file-removed file-id]
                 :on-error   [::file-remove-failed file-id]}
      :db       optimistic-db})))

(rf/reg-event-fx
 ::upload-progress
 (fn [{db :db} [_ dispatch-on-upload-progress attachments-ref-path tmp-attachment-id progress]]
   (let [loaded (.-loaded progress)
         total (.-total progress)
         tmp-attachment {:db/id    tmp-attachment-id
                         :status   (if (< loaded total)
                                     :uploading-server
                                     :uploading-cloud)
                         :percents (int (* (/ loaded total) 100))}]
     (merge {:db (assoc-in db (conj attachments-ref-path tmp-attachment-id) tmp-attachment)}
            (when dispatch-on-upload-progress
              {:fx [[:dispatch
                     (conj dispatch-on-upload-progress attachments-ref-path tmp-attachment)]]})))))

(rf/reg-event-fx
 ::upload-file-success
 (fn [{db :db}
      [_ owning-entity-ref-path attachments-ref-path tmp-attachment-id
       dispatch-on-upload-success {:keys [body]}]]
   (let [db (p/dissoc-in db (conj attachments-ref-path tmp-attachment-id))
         owning-entity (get-in db owning-entity-ref-path)
         updated-attachment-ids (-> owning-entity
                                    :common/attachments
                                    (set)
                                    (disj tmp-attachment-id)
                                    (conj (:db/id body)))
         updated-entity (update owning-entity :common/attachments updated-attachment-ids)
         db (assoc-in db owning-entity-ref-path updated-entity)
         db (assoc-in db [attachments-ref-path (:db/id body)] body)]
     {:db db
      :fx [[:dispatch (conj dispatch-on-upload-success (:db/id body))]]})))

(rf/reg-event-fx
 ::upload-file-failure
 (fn [{db :db} [_ attachments-ref-path tmp-attachment-id dispatch-on-upload-failure error]]
   (let [upload-state {:db/id  tmp-attachment-id
                       :status :error
                       :error  error}
         updated-db (assoc-in db (conj attachments-ref-path tmp-attachment-id) upload-state)]
     (merge {:db updated-db}
            (when dispatch-on-upload-failure
              {:fx [[:dispatch (conj dispatch-on-upload-failure error)]]})))))
